import { CaretLeft, CaretRight, SidebarSimple, UsersThree } from '@phosphor-icons/react';
import type { ScheduledPostDto } from '@reelplicate/api-client/model';
import { useListPersonas } from '@reelplicate/api-client/personas';
import { useListScheduledPosts } from '@reelplicate/api-client/scheduled-posts';
import { useListSocialAccounts } from '@reelplicate/api-client/social-accounts';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  IconButton,
  Inline,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectDivider,
  SelectItem,
  Stack,
} from '@reelplicate/ui';
import { useEffect, useMemo, useState } from 'react';

import { AccountsPanel } from './accounts-panel';
import {
  accountPostState,
  groupAccounts,
  postStatus,
  STATUS_LABELS,
} from './calendar-model';
import { CreatePostModal } from './create-post/create-post-modal';
import { MonthGrid } from './month-grid';
import { PlatformStack } from './platform-stack';
import { PostDetailsModal } from './post-details-modal';
import { useDragMove } from './use-drag-move';
import {
  addDays,
  addMonths,
  formatDayLong,
  formatMonth,
  formatWeekRange,
  monthGrid,
  startOfDay,
  startOfWeek,
} from './week';
import { type GridPost, WeekGrid } from './week-grid';

type View = 'day' | 'week' | 'month';
const VIEWS: View[] = ['day', 'week', 'month'];

/** What a view shows around `anchor`: its range, heading and grid columns. */
function viewRange(view: View, anchor: Date) {
  if (view === 'day') {
    const from = startOfDay(anchor);
    return { from, to: addDays(from, 1), label: formatDayLong(from) };
  }
  if (view === 'week') {
    const from = startOfWeek(anchor);
    return { from, to: addDays(from, 7), label: formatWeekRange(from) };
  }
  const { start, weeks } = monthGrid(anchor);
  return {
    from: start,
    to: addDays(start, weeks * 7),
    label: formatMonth(anchor),
    weeks,
  };
}

/** Prev/next: a day, a week or a month from `anchor`. */
function step(view: View, anchor: Date, by: -1 | 1): Date {
  if (view === 'day') return addDays(anchor, by);
  if (view === 'week') return addDays(anchor, by * 7);
  return addMonths(anchor, by);
}

const ALL = 'all';

const LEGEND = [
  { status: 'scheduled', bar: 'bg-brand-strong' },
  { status: 'published', bar: 'bg-status-success-solid' },
  { status: 'review', bar: 'bg-status-warning-solid' },
  { status: 'attention', bar: 'bg-status-warning-solid' },
  { status: 'failed', bar: 'bg-status-danger-solid' },
  { status: 'draft', bar: 'bg-border-strong' },
] as const;

/** The current time, ticking every minute (or fixed, for tests). */
function useNow(fixed?: Date): Date {
  const [now, setNow] = useState(() => fixed ?? new Date());
  useEffect(() => {
    if (fixed) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [fixed]);
  return fixed ?? now;
}

/** Lists load for the panel and the grid; one failing shouldn't take the page down. */
const QUIET = (workspaceId: string) => ({
  query: { enabled: Boolean(workspaceId), throwOnError: false },
});

/**
 * Figma "Screen — Calendar" (143:892), "Calendar · Day" (369:5217) and
 * "Calendar · Month" (370:4587): the Accounts panel and a day, week or month
 * of posts across the workspace's accounts. Flows put posts here (Schedule &
 * publish); nothing publishes yet.
 */
export function CalendarPage({ now: fixedNow }: { now?: Date } = {}) {
  const now = useNow(fixedNow);
  const [view, setView] = useState<View>('week');
  // The date the views are built around; switching views keeps it.
  const [anchor, setAnchor] = useState(() => startOfDay(now));
  const range = viewRange(view, anchor);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [group, setGroup] = useState(ALL);
  const [panelOpen, setPanelOpen] = useState(true);
  // Kept as an id so the open post follows refetches (marking one account posted).
  const [openPostId, setOpenPostId] = useState<string | null>(null);
  // Create post: closed (null), new (maybe at a picked time), or a draft to finish.
  const [composer, setComposer] = useState<{
    at?: Date;
    draft?: ScheduledPostDto;
  } | null>(null);

  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const { data: socialAccounts } = useListSocialAccounts(workspaceId, QUIET(workspaceId));
  const { data: personas } = useListPersonas(workspaceId, QUIET(workspaceId));
  const { data: scheduled } = useListScheduledPosts(
    workspaceId,
    { from: range.from.toISOString(), to: range.to.toISOString() },
    QUIET(workspaceId),
  );

  const { groups, accounts } = useMemo(
    () =>
      groupAccounts(
        { name: workspace?.name ?? 'Brand', logoUrl: workspace?.logoUrl },
        personas?.items ?? [],
        socialAccounts ?? [],
      ),
    [workspace?.name, workspace?.logoUrl, personas, socialAccounts],
  );
  const visible = useMemo(
    () => new Set(accounts.filter((a) => !hidden.has(a.id)).map((a) => a.id)),
    [accounts, hidden],
  );

  const posts = useMemo<GridPost[]>(
    () =>
      (scheduled ?? []).flatMap((post) => {
        const shown = accounts.filter(
          (a) =>
            post.accounts.some((target) => target.id === a.id) &&
            visible.has(a.id) &&
            (group === ALL || a.groupId === group),
        );
        if (shown.length === 0) return [];
        const owner = groups.find((g) => g.id === shown[0]!.groupId)!;
        return [
          {
            id: post.id,
            title: post.title,
            start: new Date(post.scheduledAt),
            account: owner.short,
            status: postStatus(post, now, new Set(shown.map((a) => a.id))),
            thumbnail: post.thumbnailUrl,
            platforms: shown.map((a) => {
              const target = post.accounts.find((t) => t.id === a.id)!;
              return {
                platform: a.platform,
                state: accountPostState(target, new Date(post.scheduledAt), now),
                failureReason: target.failureReason,
              };
            }),
            // Still to go out, or failed (moving it tries again); a move
            // into the past is refused and snaps back.
            movable: post.status === 'SCHEDULED' || post.status === 'FAILED',
          },
        ];
      }),
    [scheduled, accounts, groups, visible, group, now],
  );
  const drag = useDragMove(workspaceId, now);
  const openGridPost = (post: GridPost) => setOpenPostId(post.id);
  const openPost = scheduled?.find((p) => p.id === openPostId) ?? null;
  const openPlatforms = openPost
    ? accounts
        .filter((a) => openPost.accounts.some((target) => target.id === a.id))
        .map((a) => a.platform)
    : [];

  return (
    <div className="flex h-full">
      {panelOpen && (
        <AccountsPanel
          groups={groups}
          accounts={accounts}
          visible={visible}
          onToggleAccount={(id, show) =>
            setHidden((prev) => {
              const next = new Set(prev);
              if (show) next.delete(id);
              else next.add(id);
              return next;
            })
          }
          onCollapse={() => setPanelOpen(false)}
          onCreatePost={() => setComposer({})}
        />
      )}

      {/* Children keep their height and the column scrolls: cards with
          overflow-hidden would otherwise shrink to fit and clip. */}
      <Stack gap={4} className="min-w-0 flex-1 overflow-y-auto *:shrink-0 p-6">
        <header>
          <h1 className="text-heading-h2 text-text-primary">Calendar</h1>
          <p className="text-body-md text-text-secondary mt-0.5">
            Plan and cross-post across {accounts.length}{' '}
            {accounts.length === 1 ? 'account' : 'accounts'}. Flows with Schedule &amp;
            publish put their posts here.
          </p>
        </header>

        <Inline gap={2} wrap className="min-h-[100px]">
          {!panelOpen && (
            <IconButton
              icon={SidebarSimple}
              variant="outline"
              aria-label="Show accounts"
              onClick={() => setPanelOpen(true)}
            />
          )}
          <IconButton
            icon={CaretLeft}
            variant="outline"
            aria-label={`Previous ${view}`}
            onClick={() => setAnchor((a) => step(view, a, -1))}
          />
          <h2 aria-live="polite" className="text-label-lg text-text-primary px-2">
            {range.label}
          </h2>
          <IconButton
            icon={CaretRight}
            variant="outline"
            aria-label={`Next ${view}`}
            onClick={() => setAnchor((a) => step(view, a, 1))}
          />
          <Button variant="outline" onClick={() => setAnchor(startOfDay(now))}>
            Today
          </Button>
          <span className="flex-1" />
          <Select
            size="sm"
            leadingIcon={UsersThree}
            value={group}
            onValueChange={setGroup}
            aria-label="Filter by account"
            className="w-[180px]"
          >
            <SelectItem value={ALL}>All accounts</SelectItem>
            <SelectDivider />
            {groups.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.name}
              </SelectItem>
            ))}
          </Select>
          <SegmentedControl
            aria-label="Calendar view"
            value={view}
            onValueChange={(next) => setView(next as View)}
            className="gap-0.5 rounded-[10px] p-[3px]"
          >
            {VIEWS.map((view) => (
              <SegmentedControlItem
                key={view}
                value={view}
                className="rounded-lg px-3.5 py-1.5 capitalize"
              >
                {view}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </Inline>

        <Inline as="ul" gap={4} aria-label="Legend" wrap>
          {LEGEND.map(({ status, bar }) => (
            <Inline as="li" key={status} gap={1.5}>
              <span aria-hidden className={`h-3 w-[3px] rounded-[2px] ${bar}`} />
              <span className="text-caption text-text-secondary">
                {STATUS_LABELS[status]}
              </span>
            </Inline>
          ))}
          <Inline as="li" gap={1.5}>
            <PlatformStack platforms={['tiktok', 'instagram', 'youtube']} />
            <span className="text-caption text-text-secondary">
              Stacked icons = cross-posted
            </span>
          </Inline>
        </Inline>

        {view === 'month' ? (
          <MonthGrid
            start={range.from}
            weeks={range.weeks ?? 5}
            month={anchor}
            now={now}
            posts={posts}
            onOpenPost={openGridPost}
            onOpenDay={(day) => {
              setAnchor(day);
              setView('day');
            }}
            drag={drag}
          />
        ) : (
          <WeekGrid
            days={Array.from({ length: view === 'day' ? 1 : 7 }, (_, i) =>
              addDays(range.from, i),
            )}
            now={now}
            posts={posts}
            onOpenPost={openGridPost}
            onSchedule={(at) => setComposer({ at })}
            drag={drag}
          />
        )}
        {scheduled && posts.length === 0 && (
          <p className="text-body-sm text-text-secondary">
            Nothing scheduled this {view}.
          </p>
        )}
      </Stack>
      <PostDetailsModal
        workspaceId={workspaceId}
        post={openPost}
        platforms={openPlatforms}
        groups={groups}
        workspaceZone={workspace?.timezone}
        qrShareEnabled={workspace?.qrShareEnabled}
        now={now}
        onOpenChange={(open) => !open && setOpenPostId(null)}
        onEditDraft={(draft) => {
          setOpenPostId(null);
          setComposer({ draft });
        }}
      />
      {workspace && (
        <CreatePostModal
          open={composer !== null}
          onOpenChange={(open) => !open && setComposer(null)}
          workspace={workspace}
          groups={groups}
          accounts={socialAccounts ?? []}
          initialAt={composer?.at}
          draft={composer?.draft}
        />
      )}
    </div>
  );
}
